import { Box, Card, CardContent, CircularProgress, Divider, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

// Theme-aligned colors (see src/theme/palette.jsx)
const C = {
  text: "#121828", // text.primary / ink
  muted: "#65748B", // text.secondary
  brand: "#F3DA00", // primary.main (eStore yellow)
  surface: "#FFFFFF",
  surfaceMuted: "#F9FAFC", // background.default
  border: "#E6E8F0", // divider
  success: "#14B8A6",
  warning: "#FFB020",
  error: "#D14343",
  info: "#2196F3",
}

const formatNum = n => (typeof n === "number" ? n.toLocaleString() : n)

// When a row links to a filtered view, these turn it into an accessible,
// keyboard-operable control. Both return empty/null when no handler is given,
// so a row stays purely presentational unless a click handler is wired in.
const interactionProps = handler =>
  handler
    ? {
        role: "button",
        tabIndex: 0,
        onClick: handler,
        onKeyDown: e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handler()
          }
        },
      }
    : {}

const clickableSx = handler =>
  handler
    ? {
        cursor: "pointer",
        transition: "background-color 140ms",
        "&:hover": { bgcolor: alpha("#121828", 0.05) },
        "&:focus-visible": { outline: `2px solid ${alpha("#121828", 0.35)}`, outlineOffset: 2 },
      }
    : null

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

const WidgetCard = ({ eyebrow, title, subheader, children, sx, accent, loading = false, onHeaderClick }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: 3,
      border: `1px solid ${C.border}`,
      bgcolor: C.surface,
      transition: "border-color 180ms, transform 180ms, box-shadow 180ms",
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        borderColor: alpha(C.text, 0.18),
        transform: "translateY(-1px)",
        boxShadow: "0 6px 24px -12px rgba(18, 24, 40, 0.18)",
      },
      ...sx,
    }}
  >
    {accent && <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, bgcolor: accent }} />}
    <CardContent sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", minHeight: 0, "&:last-child": { pb: 2 } }}>
      <Box {...interactionProps(onHeaderClick)} sx={[{ mb: 1, flexShrink: 0 }, onHeaderClick && { p: 0.75, m: -0.75, borderRadius: 2 }, clickableSx(onHeaderClick)]}>
        {eyebrow && (
          <Typography variant="overline" sx={{ color: C.muted, letterSpacing: 0.8, fontSize: 10, lineHeight: 1.4, display: "block" }}>
            {eyebrow}
          </Typography>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: C.text, lineHeight: 1.3 }}>{title}</Typography>
        {subheader && (
          <Typography variant="caption" sx={{ color: C.muted, display: "block", mt: 0.25, fontSize: 11 }}>
            {subheader}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, position: "relative" }}>
        {loading ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", minHeight: 96 }}>
            <CircularProgress size={28} aria-label={`Loading ${title}`} />
          </Box>
        ) : (
          children
        )}
      </Box>
    </CardContent>
  </Card>
)

// Inline stroke icons for the Created / Despatched medallions.
const IconCreated = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 4h9a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8z" />
    <path d="M9 4v4H5" />
    <path d="M12 11v6M9 14h6" />
  </svg>
)
const IconDespatched = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7h11v9H3z" />
    <path d="M14 10h4l3 3v3h-7z" />
    <circle cx="7.5" cy="18" r="1.5" />
    <circle cx="17.5" cy="18" r="1.5" />
  </svg>
)

// A comparison bar — width is relative to the larger of the two metrics, so a
// backlog-clearing period (despatched > created) reads naturally.
const CompareBar = ({ value, max, fill, track }) => (
  <Box sx={{ height: 9, borderRadius: 4.5, bgcolor: track, overflow: "hidden" }}>
    <Box sx={{ height: "100%", width: `${(value / max) * 100}%`, bgcolor: fill, borderRadius: 4.5, transition: "width 360ms cubic-bezier(.2,.7,.2,1)" }} />
  </Box>
)

// One Created / Despatched metric row: icon medallion in front, then a
// label + value line with the comparison bar directly beneath.
const MetricRow = ({ bg, fg, icon, label, value, max, barFill, barTrack, onClick }) => (
  <Stack direction="row" alignItems="center" spacing={1.75} {...interactionProps(onClick)} sx={[onClick && { p: 0.75, m: -0.75, borderRadius: 2 }, clickableSx(onClick)]}>
    <Box sx={{ width: 44, height: 44, borderRadius: 3, bgcolor: bg, color: fg, display: "grid", placeItems: "center", flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: C.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: C.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }} noWrap>
          {formatNum(value)}
        </Typography>
      </Stack>
      <CompareBar value={value} max={max} fill={barFill} track={barTrack} />
    </Box>
  </Stack>
)

// Created (ink) vs Despatched (yellow) — two stacked metric rows whose bars
// share a scale. The metrics are independent: despatched can exceed created
// when a backlog is being cleared, and the bars flip naturally.
const CreatedDespatched = ({ created, despatched, onCreatedClick, onDespatchedClick }) => {
  const max = Math.max(created, despatched, 1)
  return (
    <Stack spacing={2.5} sx={{ flex: 1, justifyContent: "center" }}>
      <MetricRow bg={C.text} fg="#FFFFFF" icon={<IconCreated />} label="Created" value={created} max={max} barFill={C.text} barTrack={alpha(C.text, 0.07)} onClick={onCreatedClick} />
      <MetricRow bg={C.brand} fg={C.text} icon={<IconDespatched />} label="Despatched" value={despatched} max={max} barFill={C.brand} barTrack={alpha(C.brand, 0.16)} onClick={onDespatchedClick} />
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// 1. Orders — created vs despatched (follows the period filter)
// ---------------------------------------------------------------------------

export const OrdersWidget = ({ data, loading, onCreatedClick, onDespatchedClick }) => (
  <WidgetCard loading={loading} eyebrow={data.periodLabel} title="Sales Orders" subheader="Created vs despatched order volume">
    <Stack sx={{ flex: 1, justifyContent: "center" }}>
      <CreatedDespatched created={data.ordersCreated} despatched={data.ordersDespatched} onCreatedClick={onCreatedClick} onDespatchedClick={onDespatchedClick} />
    </Stack>
  </WidgetCard>
)

// ---------------------------------------------------------------------------
// 2. Units / pieces — created vs despatched
// ---------------------------------------------------------------------------

export const UnitsWidget = ({ data, loading, onCreatedClick, onDespatchedClick }) => (
  <WidgetCard loading={loading} eyebrow={data.periodLabel} title="Sales Orders: Units/Pieces" subheader="Created vs despatched piece volume">
    <Stack sx={{ flex: 1, justifyContent: "center" }}>
      <CreatedDespatched created={data.unitsCreated} despatched={data.unitsDespatched} onCreatedClick={onCreatedClick} onDespatchedClick={onDespatchedClick} />
    </Stack>
  </WidgetCard>
)

// ---------------------------------------------------------------------------
// 3. Closed Receipts — receipts physically closed + units received (split cell)
// ---------------------------------------------------------------------------

const IconReceipts = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l8 4v10l-8 4-8-4V7z" />
    <path d="M4 7l8 4 8-4" />
  </svg>
)
const IconUnitsBars = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20V4M4 20h16" />
    <path d="M8 17v-6M13 17v-9M18 17v-4" />
  </svg>
)

// One half of a split-cell stat: small medallion, label, prominent value.
const SplitCellStat = ({ bg, fg, icon, label, value, flex = 1 }) => (
  <Box sx={{ flex, minWidth: 0 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: 2.25, bgcolor: bg, color: fg, display: "grid", placeItems: "center", mb: 1 }}>{icon}</Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: C.muted, mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5, color: C.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }} noWrap>
      {formatNum(value)}
    </Typography>
  </Box>
)

export const ClosedReceiptsWidget = ({ data, loading, onHeaderClick }) => (
  <WidgetCard loading={loading} eyebrow={data.periodLabel} title="Closed Receipts" subheader="Physically received · closed receipts only" onHeaderClick={onHeaderClick}>
    <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: C.border }} />} sx={{ flex: 1, alignItems: "center" }}>
      <SplitCellStat bg={C.text} fg="#FFFFFF" icon={<IconReceipts />} label="Receipts" value={data.closedReceipts} flex={0.8} />
      <SplitCellStat bg={C.brand} fg={C.text} icon={<IconUnitsBars />} label="Units received" value={data.inboundUnits} flex={1.2} />
    </Stack>
  </WidgetCard>
)

// ---------------------------------------------------------------------------
// 6. Pending Receipts — in the system, not yet physically received (current)
// ---------------------------------------------------------------------------

const colHeadSx = { fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: C.muted }

export const PendingReceiptsWidget = ({ data, loading, onHeaderClick, onRowClick }) => (
  <WidgetCard loading={loading} eyebrow="Current" title="Pending Receipts" subheader="In system, not yet physically received" onHeaderClick={onHeaderClick}>
    {/* Centre the header+rows block so it aligns with the Order status /
        Exceptions tiles beside it (which also centre their content). */}
    <Stack spacing={0} sx={{ flex: 1, justifyContent: "center" }}>
      {data.pendingReceipts?.length > 0 && (
        <Stack direction="row" justifyContent="space-between" sx={{ pb: 0.75, borderBottom: `1px solid ${C.border}` }}>
          <Typography sx={colHeadSx}>Receipt / ASN</Typography>
          <Typography sx={colHeadSx}>Units pending</Typography>
        </Stack>
      )}

      {data.pendingReceipts.map((row, i) => {
        const rowClick = onRowClick ? () => onRowClick(row.asn) : undefined
        return (
          <Stack
            key={row.asn + i}
            direction="row"
            alignItems="center"
            spacing={1.25}
            {...interactionProps(rowClick)}
            sx={[{ py: 0.9, borderTop: i === 0 ? "none" : `1px solid ${C.border}` }, clickableSx(rowClick)]}
          >
            <Typography sx={{ width: 22, color: C.muted, fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 600 }}>{String(i + 1).padStart(2, "0")}</Typography>
            <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 13, color: C.text }} noWrap>
              {row.asn}
            </Typography>
            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: alpha(C.brand, 0.18), color: "#8a7a00", fontWeight: 700, fontSize: 12, fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
              {formatNum(row.unitsPending)}
            </Box>
          </Stack>
        )
      })}
    </Stack>
  </WidgetCard>
)

// ---------------------------------------------------------------------------
// 4. Order status breakdown — current open orders (ignores the period filter)
// ---------------------------------------------------------------------------

const statusPillColors = {
  "In Pool": "#6366F1",
  Processing: C.info,
  "On Hold": C.warning,
  Exceptions: C.error,
}

export const OrderStatusWidget = ({ data, loading, onStatusClick }) => {
  const entries = Object.entries(data.orderStatus)
  const max = Math.max(...entries.map(([, v]) => v), 1)
  const total = entries.reduce((s, [, v]) => s + v, 0)
  return (
    <WidgetCard loading={loading} eyebrow="Current" title="Order status breakdown" subheader={`${formatNum(total)} open orders across all states`}>
      <Stack spacing={1.5} sx={{ flex: 1, justifyContent: "center" }}>
        {entries.map(([label, value]) => {
          const color = statusPillColors[label] || C.info
          const rowClick = onStatusClick ? () => onStatusClick(label) : undefined
          return (
            <Box key={label} {...interactionProps(rowClick)} sx={[onStatusClick && { p: 0.75, m: -0.75, borderRadius: 2 }, clickableSx(rowClick)]}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: C.text }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: C.text }}>
                  {formatNum(value)}
                </Typography>
              </Stack>
              <Box sx={{ height: 10, bgcolor: alpha(color, 0.12), borderRadius: 5, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(value / max) * 100}%`, bgcolor: color, borderRadius: 5, transition: "width 360ms cubic-bezier(.2,.7,.2,1)" }} />
              </Box>
            </Box>
          )
        })}
      </Stack>
    </WidgetCard>
  )
}

// ---------------------------------------------------------------------------
// (Retired from the dashboard, July 2026 product session) Order mix — B2B vs
// B2C split. Kept for possible recall in a future cycle; not currently rendered.
// ---------------------------------------------------------------------------

export const B2BvsB2CWidget = ({ data }) => {
  const { b2bCount, b2cCount, b2bUnits, b2cUnits, periodLabel } = data
  const total = b2bCount + b2cCount || 1
  const b2bPct = (b2bCount / total) * 100
  const b2cPct = 100 - b2bPct
  return (
    <WidgetCard eyebrow={periodLabel} title="B2B vs B2C split" subheader="Order count and units">
      <Stack spacing={2.5} sx={{ flex: 1, justifyContent: "center" }}>
        <Box sx={{ height: 44, borderRadius: 22, overflow: "hidden", display: "flex", border: `1px solid ${C.border}` }}>
          {b2bPct > 0 && (
            <Box sx={{ width: `${b2bPct}%`, bgcolor: C.text, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14 }}>
              {b2bPct >= 12 ? `${Math.round(b2bPct)}%` : ""}
            </Box>
          )}
          {b2cPct > 0 && (
            <Box sx={{ width: `${b2cPct}%`, bgcolor: C.brand, display: "flex", alignItems: "center", justifyContent: "center", color: C.text, fontWeight: 700, fontSize: 14 }}>
              {b2cPct >= 12 ? `${Math.round(b2cPct)}%` : ""}
            </Box>
          )}
        </Box>

        {/* Legend blocks sized to their bar segment, centre-aligned. */}
        <Stack direction="row" sx={{ width: "100%" }}>
          <Box sx={{ width: `${b2bPct}%`, minWidth: 0, px: 0.5, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mb: 0.25 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: C.text, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600, letterSpacing: 0.5 }}>
                B2B
              </Typography>
            </Stack>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: C.text, lineHeight: 1.2 }}>{formatNum(b2bCount)}</Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {formatNum(b2bUnits)} units
            </Typography>
          </Box>
          <Box sx={{ width: `${b2cPct}%`, minWidth: 0, px: 0.5, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mb: 0.25 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: C.brand, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600, letterSpacing: 0.5 }}>
                B2C
              </Typography>
            </Stack>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: C.text, lineHeight: 1.2 }}>{formatNum(b2cCount)}</Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              {formatNum(b2cUnits)} units
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </WidgetCard>
  )
}

// ---------------------------------------------------------------------------
// 6. Exceptions — current open items needing action
// ---------------------------------------------------------------------------

export const ExceptionsWidget = ({ data, loading, onReasonClick, onAllExceptionsClick }) => {
  const { exceptionReasons } = data
  const max = Math.max(...exceptionReasons.map(r => r.count), 1)
  return (
    <WidgetCard loading={loading} eyebrow="Current" title="Exceptions" subheader="Open items needing action" onHeaderClick={onAllExceptionsClick}>
      <Stack spacing={1.5} sx={{ flex: 1, justifyContent: "center" }}>
        {exceptionReasons.map((r, i) => {
          const barColor = i % 2 === 0 ? C.brand : C.text
          const trackColor = i % 2 === 0 ? alpha(C.brand, 0.18) : alpha(C.text, 0.08)
          const rowClick = onReasonClick ? () => onReasonClick(r.reason) : undefined
          return (
            <Box key={r.reason} {...interactionProps(rowClick)} sx={[onReasonClick && { p: 0.75, m: -0.75, borderRadius: 2 }, clickableSx(rowClick)]}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: C.text }}>
                  {r.reason}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: C.text }}>
                  {r.count}
                </Typography>
              </Stack>
              <Box sx={{ height: 6, bgcolor: trackColor, borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${(r.count / max) * 100}%`, bgcolor: barColor, borderRadius: 3, transition: "width 360ms cubic-bezier(.2,.7,.2,1)" }} />
              </Box>
            </Box>
          )
        })}
      </Stack>
    </WidgetCard>
  )
}

// ---------------------------------------------------------------------------
// 8. Lowest days-of-stock SKUs (current)
// ---------------------------------------------------------------------------

const statusBadge = status => {
  if (status === "red") return { bg: alpha(C.error, 0.1), fg: C.error }
  if (status === "amber") return { bg: alpha(C.warning, 0.12), fg: "#A26100" }
  return { bg: alpha(C.success, 0.12), fg: "#0E8074" }
}

export const LowestStockWidget = ({ data, loading, onRowClick }) => (
  <WidgetCard loading={loading} eyebrow="Current" title="Lowest days of stock" subheader="What to reorder first">
    <Stack spacing={0} sx={{ flex: 1 }}>
      {data.lowestStock.map((row, i) => {
        const c = statusBadge(row.status)
        const rowClick = onRowClick ? () => onRowClick(row.sku) : undefined
        return (
          <Stack
            key={row.sku + i}
            direction="row"
            alignItems="center"
            spacing={1.25}
            {...interactionProps(rowClick)}
            sx={[{ py: 0.75, minWidth: 0, borderTop: i === 0 ? "none" : `1px solid ${C.border}` }, clickableSx(rowClick)]}
          >
            <Typography sx={{ width: 22, color: C.muted, fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 600 }}>{String(i + 1).padStart(2, "0")}</Typography>
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.text, lineHeight: 1.2 }} noWrap>
                {row.sku}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} noWrap>
                {row.description} · {formatNum(row.onHand)} on hand
              </Typography>
            </Box>
            <Box
              sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: 38, flexShrink: 0, textAlign: "center" }}
            >
              {row.daysOfStock}d
            </Box>
          </Stack>
        )
      })}
    </Stack>
  </WidgetCard>
)

// ---------------------------------------------------------------------------
// 7. Slow-moving lines — stalest SKUs by days since last sale (current)
// ---------------------------------------------------------------------------

const slowBadge = status => {
  if (status === "stagnant") return { bg: alpha(C.error, 0.1), fg: C.error }
  if (status === "slow") return { bg: alpha(C.warning, 0.12), fg: "#A26100" }
  return { bg: alpha(C.muted, 0.12), fg: C.muted }
}

export const SlowMovingWidget = ({ data, loading, onRowClick }) => (
  <WidgetCard loading={loading} eyebrow="Current" title="Slow moving lines" subheader="What to clear first">
    <Stack spacing={0} sx={{ flex: 1 }}>
      {data.slowMoving.map((row, i) => {
        const c = slowBadge(row.status)
        const rowClick = onRowClick ? () => onRowClick(row.sku) : undefined
        return (
          <Stack
            key={row.sku + i}
            direction="row"
            alignItems="center"
            spacing={1.25}
            {...interactionProps(rowClick)}
            sx={[{ py: 0.75, borderTop: i === 0 ? "none" : `1px solid ${C.border}` }, clickableSx(rowClick)]}
          >
            <Typography sx={{ width: 22, color: C.muted, fontVariantNumeric: "tabular-nums", fontSize: 11, fontWeight: 600 }}>{String(i + 1).padStart(2, "0")}</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.text, lineHeight: 1.2 }} noWrap>
                {row.sku}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }} noWrap>
                {formatNum(row.onHand)} on hand
              </Typography>
            </Box>
            <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: c.bg, color: c.fg, fontWeight: 700, fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: 38, textAlign: "center" }}>
              {row.daysSinceSale}d
            </Box>
          </Stack>
        )
      })}
    </Stack>
  </WidgetCard>
)
